import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import type { TechCalcProject } from "../types";
import { extractApiError } from "../utils";
import { useCreateTechCalcProject, usePatchTechCalcProject } from "../hooks";

const schema = z.object({
  name: z.string().min(1, "Podaj nazwę projektu").max(255, "Za długa nazwa"),
  description: z.string().max(2000, "Za długi opis"),
});

type FormValues = z.infer<typeof schema>;

/** Create/edit project dialog (React Hook Form + Zod). `project` set = edit mode. */
export function ProjectFormDialog({
  open,
  project,
  onClose,
  onSaved,
}: {
  open: boolean;
  project: TechCalcProject | null;
  onClose: () => void;
  /** Called with the saved project id (create → navigate into the workspace). */
  onSaved?: (id: string, created: boolean) => void;
}) {
  const create = useCreateTechCalcProject();
  const patch = usePatchTechCalcProject();
  const pending = create.isPending || patch.isPending;
  const error = create.error ?? patch.error;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: { name: project?.name ?? "", description: project?.description ?? "" },
  });

  const submit = handleSubmit((values) => {
    if (project) {
      patch.mutate(
        { id: project.id, payload: values },
        { onSuccess: (p) => { onSaved?.(p.id, false); onClose(); } },
      );
    } else {
      create.mutate(values, {
        onSuccess: (p) => { onSaved?.(p.id, true); onClose(); },
      });
    }
  });

  return (
    <Modal open={open} onClose={onClose} title={project ? "Edytuj projekt" : "Nowy projekt obliczeń"}>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="section-label">Nazwa</span>
          <input
            className="input-base mt-1"
            placeholder="np. Przebudowa linii nN — Wieś Górna"
            {...register("name")}
          />
          {errors.name && (
            <p className="mt-0.5 font-mono text-[11px] text-[var(--color-primary)]">{errors.name.message}</p>
          )}
        </label>

        <label className="block">
          <span className="section-label">Opis</span>
          <textarea
            className="input-base mt-1 min-h-[90px]"
            placeholder="Opcjonalny opis zakresu obliczeń"
            {...register("description")}
          />
          {errors.description && (
            <p className="mt-0.5 font-mono text-[11px] text-[var(--color-primary)]">
              {errors.description.message}
            </p>
          )}
        </label>

        {error != null && (
          <p className="font-mono text-xs text-[var(--color-primary)]">{extractApiError(error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" size="sm" loading={pending}>
            {project ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
